import $ from 'jquery';

import { Module } from 'lib/vendor/terrific/Module';
import { Hook } from 'lib/vendor/terrific/Sandbox';
import { getProfile } from 'lib/personalization/profile';

const SITECATALYST_BLOCKLISTED_URL_PARAMS = [
	'locationId',
	'street',
	'houseNumber',
	'houseName',
	'city',
	'zipCode',
	'zip',
	'scn',
];

const FIELDS : Record<string, {
	session : string;
	map? : keyof Address;
}> = {
	city: {session: 'scs_sa_city'},
	houseName: {session: 'scs_sa_housename'},
	houseNumber: {session: 'scs_sa_housenumber'},
	locationId: {session: 'scs_sa_locationid'},
	streetName: {session: 'scs_sa_street', map: 'street'},
	zip: {session: 'scs_sa_zipcode', map: 'zipCode4'},
};

const BADGES = {
	alert: {
		badgeClass: 'badge--notification',
		iconClass: 'icon-alert',
	},
	check: {
		badgeClass: 'badge--confirmation',
		iconClass: 'icon-check',
	},
};

type Validity = 'valid' | 'invalid' | 'partiallyValid' | 'adjusted';
type Availability = 'available' | 'restricted' | 'not_available' | 'not_specified';

export interface Address {
	zipCode6 : string;
	zipCode4 : string;
	locationId? : string;
	street? : string;
	houseNumber? : string;
	city : string;
	houseName? : string;
}

type TvAvailability = 'sd_box' | 'web_only' | 'hd_box' | 'uhd_box';

/**
 * The internal data model for the response (simplified from the actual response to fit our needs).
 */
export interface ServiceAvailabilityData {
	address : Address;
	addressValidation : Validity;
	broadbandAvailability : Availability;
	broadbandIsFiber : boolean;
	extensionInfo : string;
	extensionInfoTitle : string;
	fiberInHouse : boolean;
	fiberInHouseAvailabInfo : string;
	fiberInHouseAvailabTitle : string;
	maxDown : string;
	maxUp : string;
	productAvailability : {
		maxDown : string;
		maxUp : string;
		siebelId : string;
		availability : Availability;
		availabilityText : string;
		name : string;
	}[];
	productAvailabilityInfo : any[];
	requestType : 'none' | 'address';
	serverError : boolean;
	socketNotInLivingArea : boolean;
	technology : string;
	tvAvailability? : Availability | null;
	tvAvailabilityStatus : TvAvailability;
	tvAvailabilityInfoText : string;
	tvAvailabilityText : string;
	tvAvailabilityTitle : string;
	tvAvailabilityDescription : string;
}

interface ServiceAvailabilitySuccessResponse {
	error : undefined;
	addressValidationResult? : {
		validationInfo : Validity;
		outputAddress : Address;
		inputAddress : Address;
	};
	productAvailabilityInfo? : {
		siebelId : string;
		availabilityStatus : Availability;
		availabilityText : string;
		bondingEligible : boolean;
		maxProductUpSpeed : string;
		maxProductDownSpeed : string;
	}[];
	broadbandInfo? : {
		tvAvailabilityInfo : {
			tvAvailabilityStatus : TvAvailability;
			tvStreamsText : string;
		};
		BEPInfoHeader : string;
		BEPInfo : string;
		extensionInfoHeader : string;
		extensionInfo? : string;
		available : boolean | null;
		technology : string;
		socketNotInLivingArea : boolean | 'true' | 'false';
		maxUpSpeed : string;
		maxDownSpeed : string;
		maxDownSpeedAndBonding : string | null;
		displayLink : boolean;
		shortageInfoHeader : string;
		shortageInfo : string;
	};
}

interface ServiceAvailabilityErrorResponse {
	error : string | Error;
}

export type ServiceAvailabilityResponse = ServiceAvailabilitySuccessResponse | ServiceAvailabilityErrorResponse;

interface Request {
	address : Address;
	language : string;
	productsToCheck? : string[];
}

export interface SiebelProduct {
	name : string;
	siebelId : string;
}

/*
 * This object represents a response of the map service
 * The object is standardized so that small changes in the service do not create the need for a complete refactoring
 * The object is also used by Tc.Module.OrderOverlay => all changes must be reflected there!
 * FIXME: Make response nullable instead of providing dummy data
 */
const EMPTY_RESPONSE : Partial<ServiceAvailabilityData> = {
	address: {} as any,
	addressValidation: 'invalid',
	broadbandAvailability: 'not_available',
	broadbandIsFiber: false,
	extensionInfo: '',
	maxDown: '',
	maxUp: '',
	productAvailability: [],
	requestType: 'none',
	serverError: true, // Initially true, will be set false if valid data is returned from service
	socketNotInLivingArea: false,
	technology: '',
	tvAvailability: 'not_specified', // Not_specified means, we don't show any info for tv
	tvAvailabilityInfoText: '',
	tvAvailabilityText: '',
};

/**
 * Service Availability, a.k.a. Fiber Checker.
 * Displays the Internet speed and product availability of a specific location,
 * based on a street address.
 * Calls the GAIA service (for address lookup) and the MAP lineinfo service.
 *
 * @author Mad, Michael & Cesar Keller, and many more
 * @namespace Tc.Module
 * @class ServiceAvailability
 * @extends Tc.Module
 */
export default class ServiceAvailability extends Module {
	private $additionalInformation! : JQuery;
	private $form! : JQuery;
	private $street! : JQuery;
	private $houseNumber! : JQuery;
	private $zip! : JQuery;
	private $city! : JQuery;
	private $houseName! : JQuery;
	private $errordiv! : JQuery;
	private $internetSpeed! : JQuery;
	private $availabilityTv! : JQuery;
	private $availabilityInfo! : JQuery;
	private $availabilityInternet! : JQuery;
	private $availabilityFiber! : JQuery;
	private $result! : JQuery;
	private $address! : JQuery;
	private $resultAddress! : JQuery;
	private $extensioninfoholder! : JQuery;
	private $bondingInfoHolder! : JQuery;
	private $extensionBondingOpener! : JQuery;
	private $extensionBondingCloser! : JQuery;
	private $extensioninfoopener! : JQuery;
	private $extensioninfocloser! : JQuery;
	private $extensioninfo! : JQuery;
	private $extensioninfounavailable! : JQuery;
	private $extensionText! : JQuery;
	private $anotherCheck! : JQuery;
	private $fiberInHouseAvailability! : JQuery;
	private $contentCollapsable! : JQuery;
	private $extensionCollapsable! : JQuery;
	private $bondingInfoText! : JQuery;

	private openExtensionInfo! : string;
	private closeExtensionInfo! : string;

	private generalAddressErrorMsg! : string;
	private serverErrorMsg! : string;

	private language! : string;
	private showFiber! : string;
	private hideFiberNotAvailable! : string;
	private hideTV! : string;
	private hideInternet! : string;
	private hideExtensionInfo! : string;
	private doTracking! : boolean;
	private serviceUrl! : string;
	private productTitlePlaceholder! : string;
	private productTitle! : string;
	private productIds! : SiebelProduct[];

	private response! : ServiceAvailabilityData;
	private request? : Request;

	private checkIsRunning = false;
	private filledWithSessionStorageData = false;

	private loadingTimeout = 0;

	private formApi! : {
		cancelMapDataCall() : void;
	};

	/**
	 * @method on
	 * @param {Function} callback function
	 * @return void
	 */
	public on(callback : Hook) {
		const $ctx = this.$ctx;

		// AdditionalInformation div
		this.$additionalInformation = $ctx.find('.additionalInformation');

		// Input elements of DOM
		this.$form = $ctx.find('div.modScForm');
		this.$street = $ctx.find('div.street-name');
		this.$houseNumber = $ctx.find('div.house-number');
		this.$zip = $ctx.find('div.zip');
		this.$city = $ctx.find('div.city');
		this.$houseName = $ctx.find('div.house-name');

		// Error elements of DOM
		this.$errordiv = $ctx.find('div.error');
		this.generalAddressErrorMsg = $ctx.find('div[data-general-address-error]').data('general-address-error');
		this.serverErrorMsg = $ctx.find('div.error[data-server-error]').data('server-error');

		// Result element of DOM
		this.$internetSpeed = $ctx.find('span.internetspeed');
		this.$availabilityTv = $ctx.find('div.availability-tv');
		this.$availabilityInfo = $ctx.find('div.availability-shortage');
		this.$availabilityInternet = $ctx.find('div.availability-internet');
		this.$availabilityFiber = $ctx.find('div.availability-fiber');
		this.$result = $ctx.find('div.result');
		this.$address = $ctx.find('p.address');
		this.$resultAddress = $ctx.find('div.result p.address span.tested-address');
		this.$extensioninfoholder = $ctx.find('div.extension-info-holder');
		this.$bondingInfoHolder = $ctx.find('div.bonding-info-holder');
		this.openExtensionInfo = '.open-extension-info';
		this.closeExtensionInfo = '.close-extension-info';
		this.$extensionBondingOpener = this.$bondingInfoHolder.find(this.openExtensionInfo);
		this.$extensionBondingCloser = this.$bondingInfoHolder.find(this.closeExtensionInfo);
		this.$extensioninfoopener = this.$extensioninfoholder.find(this.openExtensionInfo);
		this.$extensioninfocloser = this.$extensioninfoholder.find(this.closeExtensionInfo);
		this.$extensioninfo = this.$extensioninfoholder.find('div.extension-info');
		this.$extensioninfounavailable = $ctx.find('div.extension-info.unavailable');
		this.$extensionText = $ctx.find('div.extension-text');
		this.$anotherCheck = $ctx.find('a.change-address');
		this.$fiberInHouseAvailability = $ctx.find('div.availability-house-fiber');
		this.$contentCollapsable = $ctx.parent('.serviceavailability').siblings('.modContactCollapsable');

		this.$extensionCollapsable = this.$contentCollapsable.children('.content');
		this.$bondingInfoText = $ctx.find('.bonding-info-holder .extension-info');

		// Retrieve data from DOM
		this.language = $ctx.data('language');
		this.showFiber = $ctx.data('showfiber');
		this.hideFiberNotAvailable = $ctx.data('hidefibernotavailable');
		this.hideTV = $ctx.data('hidetv');
		this.hideInternet = $ctx.data('hideinternet');
		this.hideExtensionInfo = $ctx.data('hideextensioninfo');
		this.doTracking = $ctx.data('dotracking');
		this.serviceUrl = $ctx.data('serviceurl') || '/map-api/onlinenslg/lineinfo';
		this.productTitlePlaceholder = $ctx.find('h3, h2').data('product-title-placeholder'); // "The product"
		this.productTitle = $('h1:not(.scs-pageheader-accessibility,:empty):first').clone().children().remove().end().text().replace(/(\n|\s+$)/g, '');
		this.productIds = []; // Fill with objects {'name': 'Product Name', 'siebelId': 'SIEBELID'}

		// Clone empty response as initial value
		this.response = $.extend(true, {}, EMPTY_RESPONSE) as ServiceAvailabilityData;

		// Rewrite all IDs (and for-attributes and href="#…") within the module as they could be in the page twice
		// (e.g. once in the orderoverlay and once on the page)
		this.$ctx.find('[id]').each((_i, el) => {
			const $el = $(el);
			$el.attr('id', `${$el.attr('id')}_${this.id}`);
		});
		this.$ctx.find('label[for]').each((_i, el) => {
			const $el = $(el);
			$el.attr('for', `${$el.attr('for')}_${this.id}`);
		});
		this.$ctx.find('a[href]').each((_i, el) => {
			const $el = $(el);
			const href = $el.attr('href') ?? '';
			if (href.indexOf('#') === 0 && href.length > 1) {
				$el.attr('href', `${href}_${this.id}`);
			}
		});

		// If we have a product to check - use different text
		this.adjustSubmitButtonText();

		// Click on "another address"
		this.$anotherCheck.click(event => {
			event.preventDefault();
			this.$contentCollapsable.addClass('hidden');
			this.checkIsRunning = false;
			this.showForm();
			this.hideAllResultElements();
		});

		this.$ctx.on('click', this.openExtensionInfo, event => {
			const $this = $(event.currentTarget);
			const $extensionInfo = $this.siblings('div.extension-info');
			event.preventDefault();
			$extensionInfo.removeClass('hidden');
			$extensionInfo.slideDown(200, this.centerOverlay.bind(this));
			setTimeout(this.centerOverlay.bind(this), 50);
			setTimeout(this.centerOverlay.bind(this), 100);
			setTimeout(this.centerOverlay.bind(this), 150);
			$(this).hide();
			$(this).siblings('a').show();
		});

		this.$ctx.on('click', this.closeExtensionInfo, event => {
			const $this = $(event.target);
			const $extensionInfo = $this.siblings('div.extension-info');

			event.preventDefault();
			$extensionInfo.slideUp(200, this.centerOverlay.bind(this));
			setTimeout(this.centerOverlay.bind(this), 50);
			setTimeout(this.centerOverlay.bind(this), 100);
			setTimeout(this.centerOverlay.bind(this), 150);
			$this.hide();
			$this.siblings('a').show();
		});

		// Search Event handler
		this.$form.off('submit');
		this.$ctx.on('submit', 'form', event => {
			this.blockSubmit();
			this.handleSubmit(event);
		});

		callback();
	}

	public after() {
		this.formApi = this.$form.data('form-api');

		this.$ctx.find('form').off('submit');

		// Read values saved in sessionStorage if the module is not a hidden dialog
		if (!(this.$ctx.is('.modOrderOverlay *') && !this.$ctx.is(':visible'))) {
			this.readValuesSavedInSessionStorage();
		}
	}

	private blockSubmit() {
		this.$ctx.find('button[type=submit]').prop('disabled', true);
	}

	private unBlockSubmit() {
		this.$ctx.find('button[type=submit]').prop('disabled', false);
	}

	private readValuesSavedInSessionStorage() {
		if (hasSessionStorageData()) {
			this.formApi.cancelMapDataCall();
			this.fillWithSessionStorageData();
		}
	}

	/**
	 * Collects data and sends it to mod.sendData
	 */
	private handleSubmit(event : {target : JQuery<HTMLFormElement>} | JQuery.SubmitEvent) {
		// No event is set when we call the method manually
		if (event && 'preventDefault' in event) {
			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();
		}

		let nrOfErrors = 0;

		// See which form was submitted
		const form = $(event.target);
		const inputs = (form.find('input') as JQuery<HTMLInputElement>).not('[name="nostreet_label"]');
		const requestType = form.data('requestType');
		this.checkIsRunning = requestType; // Remember for additional calls

		if (inputs.length === 0) {
			this.unBlockSubmit();

			return undefined;
		}

		// Make sure only visible fields are actually required to fill in
		// (the address form has a mode with street and number or with house name)
		inputs.filter(':text, textarea')
			.prop('required', true)
			.filter(':not(:visible)')
			.prop('required', false);

		// Validation => each field is needed before submission happens
		inputs.filter((_i, el) => {
			return el.required;
		}).each((_i, el) => {
			if ($(el).val() === '') {
				nrOfErrors += 1;
				$(el).closest('.field > div').addClass('error');

				if (nrOfErrors < 2) {
					this.$errordiv.find('span').text($(el).closest('.field > div').data('errormessage'));
				} else {
					this.$errordiv.removeClass('hidden').find('span').text(this.generalAddressErrorMsg);
				}
			} else {
				$(el).closest('.field > div').removeClass('error');
			}
		});

		if (nrOfErrors > 0) {
			this.$errordiv.removeClass('hidden');
			this.centerOverlay();
			this.unBlockSubmit();

			return false;
		}

		this.removeAllErrorClass();
		this.centerOverlay();

		const jsonObject = this.prepareRequest(inputs);

		this.sendData(jsonObject);

		return undefined;
	}

	private prepareRequest($inputs? : JQuery<HTMLInputElement>) {
		const addressObject = {} as any as Address;

		$inputs = $inputs ?? (this.$ctx.find('.addressFields > form input') as JQuery<HTMLInputElement>).not('[name="nostreet_label"]');

		let noStreet = false;

		// Collect data and send it to webservice
		$inputs.each((_i, el) => {
			const $input = $(el);
			const name = $input.attr('name');
			const value = $input.val() as string;

			switch(name) {
				case 'nostreet':
					noStreet = $input.is(':checked');
					break;
				default:
					if(name && FIELDS[name]) {
						const key = FIELDS[name].map ?? name as keyof Address;
						addressObject[key] = value;
					}
					break;
			}
		});

		if (noStreet) {
			delete addressObject.street;
			delete addressObject.houseNumber;
		} else {
			delete addressObject.houseName;
		}

		const jsonObject : Request = {
			address: addressObject,
			language: this.language,
			productsToCheck: undefined as (string[] | undefined),
		};

		for(const product of this.productIds) {
			jsonObject.productsToCheck = jsonObject.productsToCheck || [];
			jsonObject.productsToCheck.push(product.siebelId);
		}

		return jsonObject;
	}

	/**
	 * Sends data to service
	 * uses mod.readServiceData to get structured data into mod.response
	 */
	private sendData(saObject : Request) {
		this.startLoading();

		// Write all data in sessionStorage for later use in the same browser session
		if (window.sessionStorage) {
			const addr = saObject.address || {};
			for (const field in FIELDS) {
				// Jshint forin: false
				const key = FIELDS[field].map ?? field as keyof Address;
				sessionStorage.setItem(
					FIELDS[field].session,
					addr[key] ?? '',
				);
			}
		}

		const success = (data : ServiceAvailabilityResponse) => {
			this.unBlockSubmit();
			this.log('callService > success', arguments);
			this.endLoading();
			this.readServiceData(data); // Fills mod.response
			this.renderResult();
			this.centerOverlay();
			// Important, that this is done last, as the overlay first hides a lot of things and just redisplays them depending on the data
			this.sendDataUpdate(data);
			// Hide "more information" link if property in broadbandInfo "displayLink" is false
			this.displayLinkChecker(data);
			this.fillShortageInfo(data);
			this.showBondingInformation(data);
		};

		const customError = ()  => {
			this.unBlockSubmit();
			this.endLoading();

			this.$errordiv.removeClass('hidden');
			this.$errordiv.find('span').text(this.serverErrorMsg);
		};

		this.request = saObject;

		callService(saObject, this.serviceUrl, success, customError);
	}

	private displayLinkChecker(data : ServiceAvailabilityResponse) {
		const $ctx = this.$ctx;
		const $moreInfo = $ctx.find('.extension-info .scs-primary-link');

		if (data && 'broadbandInfo' in data && data.broadbandInfo) {
			if (!data.broadbandInfo.displayLink) {
				$moreInfo.hide();
			} else {
				$moreInfo.show();
			}
		}
	}

	private showBondingInformation(response : ServiceAvailabilityResponse) {
		const $ctx = this.$ctx;
		const $bondingInformation = $ctx.find('.bonding-information');
		$bondingInformation.hide();

		if(response.error) {
			return;
		}

		const data = response as ServiceAvailabilitySuccessResponse;

		const $bondingText = $ctx.find('.bonding-text');
		const $extInfo = $bondingInformation.find('.extension-info');
		const hasBondingInfo = data.broadbandInfo && data.broadbandInfo.maxDownSpeedAndBonding !== null && !/FIBER/.test(data.broadbandInfo.technology);
		const maxDownSpeed = data.broadbandInfo?.maxDownSpeed ? SCS.utils.convertToBits(data.broadbandInfo.maxDownSpeed) : undefined;
		const maxUpSpeed = data.broadbandInfo?.maxUpSpeed ? SCS.utils.convertToBits(data.broadbandInfo.maxUpSpeed) : undefined;
		const isSmeSegment = $('body').attr('data-page-segment') === 'sme';

		$bondingInformation.hide();

		if(!isSmeSegment) {
			// Backend checking for location bonding information is only available for RES.
			if(hasBondingInfo && (!data.productAvailabilityInfo?.length || data.productAvailabilityInfo[0].bondingEligible)) {
				$bondingInformation.show();
				$bondingText.text(data.broadbandInfo!.maxDownSpeedAndBonding!);
				$extInfo.hide();
				this.$extensionBondingOpener.show();
				this.$extensionBondingCloser.hide();
			}
		} else if((maxUpSpeed && maxUpSpeed <= 4000000) || (maxDownSpeed && maxDownSpeed <= 10000000)) {
			// Hence poor mans check for SME bonding information.
			$bondingInformation.show();
		}
	}

	/**
	 * Takes data from service and fills mod.response with it
	 */
	// tslint:disable-next-line: cyclomatic-complexity // Someone elese refactor this…
	private readServiceData(response : ServiceAvailabilityResponse) {
		// Clone empty response as initial value
		this.response = $.extend(true, {}, EMPTY_RESPONSE) as ServiceAvailabilityData;

		// Check for general errors
		if (!response || response.error) {
			this.response.serverError = true;
			this.log('readServiceresponse > error', response);

			return;
		}

		const data = response as ServiceAvailabilitySuccessResponse;
		this.response.serverError = false;

		// Check input validation
		if (data.addressValidationResult) {
			this.response.requestType = 'address';
			this.response.addressValidation = data.addressValidationResult.validationInfo;
			if (data.addressValidationResult.outputAddress) { // If output address is not set, keep the empty object
				this.response.address = data.addressValidationResult.outputAddress;
				this.response.address.zipCode6 = this.response.address.zipCode4;
				if (this.response.address.zipCode4 && this.response.address.zipCode4.length > 4) {
					this.response.address.zipCode4 = this.response.address.zipCode4.substr(0, 4);
				}
				this.$ctx.toggleClass('noLocationId', data.addressValidationResult.outputAddress.locationId === '');
			} else {
				this.$ctx.toggleClass('noLocationId', data.addressValidationResult.inputAddress.locationId === '');
			}
		} else {
			this.response.serverError = true;

			return;
		}
		/**
		 * siebelId			- String of the products siebelId
		 * availabilityStatus - String with the folowing possible values: 'available'|'restricted'|'not_available'
		 * availabilityText 	- String with translated text for the different status
		 * maxUp				- String max upload speed: '100 Mbit/s'
		 * maxDown				- String max download speed: '1 Gbit/s'
		 */
		// Fill products first - need this info anyway
		if (this.productIds && this.productIds.length > 0 && (data.productAvailabilityInfo?.length ?? 0) > 0) {
			for(const productAvailability of data.productAvailabilityInfo!) {
				this.response.productAvailability.push({
					availability: productAvailability.availabilityStatus,
					availabilityText: productAvailability.availabilityText,
					maxDown: productAvailability.maxProductDownSpeed,
					maxUp: productAvailability.maxProductUpSpeed,
					name: this.getProductName(productAvailability.siebelId),
					siebelId: productAvailability.siebelId,
				});
			}
		}

		// Check tv availability
		if (data.broadbandInfo && data.broadbandInfo.tvAvailabilityInfo) {
			this.response.tvAvailabilityInfoText = data.broadbandInfo.tvAvailabilityInfo.tvStreamsText;
			this.response.tvAvailabilityStatus = data.broadbandInfo.tvAvailabilityInfo.tvAvailabilityStatus;
			this.response.tvAvailability = tvAvailability(data);
			this.response.tvAvailabilityTitle = this.$ctx.data(`translation-tvstatus-${this.response.tvAvailabilityStatus}-title`);
			this.response.tvAvailabilityDescription = this.$ctx.data(`translation-tvstatus-${this.response.tvAvailabilityStatus}-title`);
		} else {
			this.response.tvAvailability = 'not_specified';
		}

		// Check fiber in house availability
		if (data.broadbandInfo && data.broadbandInfo.BEPInfoHeader && data.broadbandInfo.BEPInfo) {
			this.response.fiberInHouse = true;
			this.response.fiberInHouseAvailabTitle = data.broadbandInfo.BEPInfoHeader;
			this.response.fiberInHouseAvailabInfo = data.broadbandInfo.BEPInfo;
		} else {
			this.response.fiberInHouse = false;
		}

		// Check broadband availability
		if (data.broadbandInfo && data.broadbandInfo.extensionInfo && data.broadbandInfo.extensionInfoHeader) {
			this.response.extensionInfoTitle = data.broadbandInfo.extensionInfoHeader;
			this.response.extensionInfo = data.broadbandInfo.extensionInfo;
		}
		if (!data.broadbandInfo || data.broadbandInfo.available === false) {
			this.response.broadbandAvailability = 'not_available';

			return;
		}

		// Broadband is available
		this.response.broadbandAvailability = 'available'; // Will be overwritten with restricted if product is restricted
		this.response.broadbandIsFiber = /FIBER/.test(data.broadbandInfo.technology);
		this.response.technology = data.broadbandInfo.technology ? data.broadbandInfo.technology.toLowerCase() : '';
		this.response.socketNotInLivingArea = data.broadbandInfo.socketNotInLivingArea && String(data.broadbandInfo.socketNotInLivingArea) === 'true';
		this.response.maxUp = data.broadbandInfo.maxUpSpeed;
		this.response.maxDown = data.broadbandInfo.maxDownSpeed;

		// If there is just one product and it is restricted or not available => broadbandAvailability must also be restricted
		if (this.response.productAvailability && this.response.productAvailability.length === 1 && this.response.productAvailability[0].availability !== 'available') {
			this.response.broadbandAvailability = 'restricted';
		}
	}

	/**
	 * Renders the data that is available in mod.response
	 */
	private renderResult() {
		const response = this.response;

		if (response.serverError) {
			this.invalidServerErrorMessage();

			return;
		}

		if (!isValidResponse(response)) {
			// Invalid input - show form again
			this.removeAllErrorClass();
			this.invalidFormErrorMessage();
		} else {
			// Request was valid - write data
			this.hideForm();

			this.showAllResultElements();
			this.fillResultAddressBox();
			this.fillForm();

			this.showFiberAvailability();
			this.showInternetAvailability();
			this.showTvAvailability();
			this.showFiberInHouse();
			this.fillExtensionInfo();
			this.removeAllErrorClass();
			this.addParametersToLinks();
		}
	}

	private isInOverlay() {
		return this.$ctx.is('.ui-dialog *') || this.$ctx.is('.is-mobile .modOrderOverlay *');
	}

	// Fill original form with data from JSON
	private fillForm() {
		this.$street.find('input').val(this.response.address.street ?? '');
		this.$houseNumber.find('input').val(this.response.address.houseNumber ?? '');
		this.$zip.find('input').val(this.response.address.zipCode4);
		this.$city.find('input').val(this.response.address.city);
		this.$houseName.find('input').val(this.response.address.houseName ?? '');
	}

	// Fills the result Address
	private fillResultAddressBox() {
		const street = [];
		const address = [];
		const request = this.request || {} as any as Request;
		const response = this.response || {};
		const requestAddress = request.address || {} as any as Address;
		const responseAddress = response.address || {} as any as Address;

		this.$address.removeClass('hidden');

		// We should only get either street and houseNumber
		// or a houseName, but handle all cases gracefully
		if (responseAddress.street) {
			street.push(responseAddress.street);
		} else if (requestAddress.street) {
			street.push(requestAddress.street);
		}
		if (responseAddress.houseName) {
			street.push(responseAddress.houseName);
		} else if (requestAddress.houseName) {
			street.push(requestAddress.houseName);
		}
		if (responseAddress.houseNumber) {
			street.push(responseAddress.houseNumber);
		} else if (requestAddress.houseNumber) {
			street.push(requestAddress.houseNumber);
		}
		// This should probably never ever happen
		// but the API can act weird sometimes
		// avoids printing "null" to the user
		if (street.length > 0) {
			address.push(street.join(' '));
		}

		// We always need a zip and city
		address.push([
			this.response.address.zipCode4,
			this.response.address.city,
		].join(' '));

		this.$resultAddress.text(address.join(', '));
	}

	private fillShortageInfo(response : ServiceAvailabilityResponse) {
		const $name = this.$availabilityInfo.find('.name');
		const $desc = this.$availabilityInfo.find('.desc');

		if(!response || response.error) {
			return;
		}

		const data = response as ServiceAvailabilitySuccessResponse;

		if (data && data.broadbandInfo && data.broadbandInfo.shortageInfoHeader && data.broadbandInfo.shortageInfo) {
			this.$availabilityInfo.removeClass('hidden');
			$name.text(data.broadbandInfo.shortageInfoHeader);
			$desc.text(data.broadbandInfo.shortageInfo);
		} else {
			this.$availabilityInfo.addClass('hidden');
		}
	}

	// Fills the blue box
	private fillExtensionInfo() {
		this.$extensioninfoholder.addClass('hidden');
		if (this.hideExtensionInfo) {
			return;
		}

		if (!this.$extensioninfoholder.hasClass('no-collapse')) {
			this.$extensioninfounavailable.hide().removeClass('hidden');
		}

		// New extension info is just a string
		if (this.response.extensionInfo) {
			this.$extensioninfoholder.removeClass('hidden');
			this.$extensioninfoholder.find('.extension-info-title').text(this.response.extensionInfoTitle);
			this.$extensionText.text(this.response.extensionInfo);
			this.$extensioninfoopener.show();
			this.$extensioninfocloser.hide();
			this.$extensionBondingOpener.show();
			this.$extensionBondingCloser.hide();
			if (!this.$extensioninfoholder.hasClass('no-collapse')) {
				this.$extensioninfo.hide(); // Close
				this.$extensionCollapsable.hide().siblings('.contact').children('a').removeClass('opened'); // Close content collapsable
				this.$bondingInfoText.hide();
			} else {
				this.$extensioninfo.show();
				this.$extensioninfounavailable.addClass('hidden');
			}

			// Hide the link if fiber is in front-of the house, but the house is not
			// yet enabled with a fiber outlet.
			this.$extensioninfoholder.find('.extension-info .link').toggleClass('hidden', this.response.fiberInHouse);
		} else if (!this.response.broadbandIsFiber) {
			this.$extensioninfounavailable.show();
		}
	}

	/**
	 * Display Fiber availability information
	 */
	private showFiberAvailability() {
		// Fiber
		if (this.showFiber) {
			this.$availabilityFiber.addClass('hidden');

			if (this.response.broadbandIsFiber) {
				this.$availabilityFiber.find('.desc-text').text(this.$ctx.data('translation-fiberstatus-available-description'));
				this.$availabilityFiber.removeClass('hidden');
			} else if (!this.hideFiberNotAvailable) {
				this.$availabilityFiber.find('.desc-text').text(this.$ctx.data('translation-fiberstatus-none-description'));
				this.$availabilityFiber.removeClass('hidden');
			}
		} else {
			this.$availabilityFiber.addClass('hidden');
		}
	}

	/**
	 * Display TV availability information
	 */
	private showTvAvailability() {
		if(this.hideTV) {
			this.$availabilityTv.addClass('hidden');

			return;
		}

		switch(this.response.tvAvailability) {
			// Both these cases are handled the same way. The text is different, however.
			case 'available':
			case 'restricted':
				this.$availabilityTv.removeClass('hidden');
				this.$availabilityTv.find('.name-text').text(this.response.tvAvailabilityTitle);
				this.$availabilityTv.find('.desc-text').text(this.response.tvAvailabilityDescription);
				this.$availabilityTv.addClass('available-state');
				break;
			case 'not_available':
				this.$availabilityTv.addClass('hidden');
				break;
			default: // 'not_specified'
				this.$availabilityTv.addClass('hidden'); break;
		}

		const badgeStatus = (this.response.tvAvailability === 'available') ? 'check' : 'alert';
		showAvailabilityBadge(this.$availabilityTv, badgeStatus);
	}

	/**
	 * Shows the availabilty information for fiber in the house.
	 */
	private showFiberInHouse() {
		if (this.response.fiberInHouse) {
			this.$fiberInHouseAvailability.removeClass('hidden');
			this.$fiberInHouseAvailability.find('.name').text(this.response.fiberInHouseAvailabTitle);
			this.$fiberInHouseAvailability.find('.desc').text(this.response.fiberInHouseAvailabInfo);
		} else {
			this.$fiberInHouseAvailability.find('.desc span').remove();
			this.$fiberInHouseAvailability.addClass('hidden');
		}
	}


	/**
	 * Dispaly intennet availability information
	 */
	private showInternetAvailability() {
		const $downSpeed = this.$availabilityInternet.find('.downloadspeed');
		const $upSpeed = this.$availabilityInternet.find('.uploadspeed');
		let isServiceRestricted = false;

		// The editor said hide internet info, so hide it for sure.
		if(this.hideInternet) {
			this.$availabilityInternet.addClass('hidden');

			return;
		}

		// Check whether internet is available at all
		if (this.response.broadbandAvailability === 'not_available') {
			this.$availabilityInternet.find('.desc').hide();
			this.$availabilityInternet.find('.desc-not-available').show();
			isServiceRestricted = true;
		} else {
			this.$availabilityInternet.find('.desc').show();
			this.$availabilityInternet.find('.desc-not-available').hide();

			$downSpeed.find('.real').text($downSpeed.find('.real').data('text').replace(/%speed%/ig, this.response.maxDown));
			$upSpeed.find('.real').text($upSpeed.find('.real').data('text').replace(/%speed%/ig, this.response.maxUp));

			if (this.response.productAvailability &&
				this.response.productAvailability.length === 1 &&
				this.response.productAvailability[0].availability !== 'available') {

				// Show download information
				const down = this.response.productAvailability[0].maxDown;
				if (SCS.utils.convertToBits(down) > SCS.utils.convertToBits(this.response.maxDown)) {
					this.writeInternetSpeed($downSpeed, down);
					isServiceRestricted = true;
				} else {
					$downSpeed.find('.insteadof').hide();
				}

				// Show upload information
				const up = this.response.productAvailability[0].maxUp;
				if (SCS.utils.convertToBits(up) > SCS.utils.convertToBits(this.response.maxUp)) {
					this.writeInternetSpeed($upSpeed, up);
					isServiceRestricted = true;
				} else {
					$upSpeed.find('.insteadof').hide();
				}

			} else {
				$downSpeed.find('.insteadof').hide();
				$upSpeed.find('.insteadof').hide();
			}
		}

		const badgeStatus = (isServiceRestricted) ? 'alert' : 'check';
		showAvailabilityBadge(this.$availabilityInternet, badgeStatus);
	}

	private writeInternetSpeed($container : JQuery, speed : string) {
		$container.find('.insteadof').show().text(
			$container.find('.insteadof').data('text')
				.replace(/%speed%/ig, speed)
				.replace(/%product%/ig, this.response.productAvailability[0].name),
		);
	}

	// Gets Product name - either from the page title or the products-array which can be filled by other modules
	private getProductName(productSiebelId : string) {
		for (const product of this.productIds) {
			if (product.siebelId === productSiebelId && product.name) {
				return product.name;
			}
		}

		return this.productTitle;
	}

	// Show error message, address is invalid (and fill back)
	private invalidFormErrorMessage() {
		const errorMessage = this.generalAddressErrorMsg;

		this.hideAllResultElements();
		this.$errordiv.removeClass('hidden');
		this.$errordiv.find('span').text(errorMessage);
	}

	private invalidServerErrorMessage() {
		const errorMessage = this.serverErrorMsg;

		this.hideAllResultElements();
		this.$errordiv.removeClass('hidden');
		this.$errordiv.find('span').text(errorMessage);
	}

	private hideAllResultElements() {
		this.$result.addClass('hidden');
		this.centerOverlay();
	}

	private hideForm() {
		this.sendStatusUpdate('result');
		this.$form.addClass('hidden');
		this.$contentCollapsable.removeClass('hidden');
	}

	private showForm() {
		this.sendStatusUpdate('form');
		this.$form.removeClass('hidden');
	}

	private removeAllErrorClass() {
		this.$form.find('div.field, div.field > div').removeClass('error');
		this.$errordiv.addClass('hidden');
		this.$errordiv.find('span').text();
		this.$form.find('input[aria-invalid="true"]').removeAttr('aria-invalid');
	}

	private showAllResultElements() {
		this.$address.removeClass('hidden');
		this.$internetSpeed.removeClass('hidden');
		this.$availabilityTv.removeClass('hidden');
		this.$availabilityInternet.removeClass('hidden');
		this.$result.removeClass('hidden');
		this.$resultAddress.removeClass('hidden');
		this.$additionalInformation.addClass('hidden');
	}

	private startLoading() {
		this.loadingTimeout = window.setTimeout(() => {
			if (this.$ctx.find('.loading').length === 0) {
				this.$ctx.append('<div class="loading" />');
			}
			this.$ctx.find('.loading').css('height', this.$ctx.height() + 'px').fadeTo(200, 1);
			this.$form.fadeTo(200, 0);
		}, 300);
	}

	private endLoading() {
		window.clearTimeout(this.loadingTimeout);
		this.$ctx.find('.loading').hide();
		this.$form.fadeTo(200, 1);
	}

	/**
	 * Simple check if fields are ready to submit
	 */
	private inputIsValid() {
		if (this.checkIsRunning) {
			return this.checkIsRunning;
		}

		const fields = this.$form
			.find('.addressFields input:visible:text')
			.get();

		for(const field of fields) {
			if ($(field).val() === '') {
				return false;
			}
		}

		return true;
	}

	private fillWithSessionStorageData() {
		const preFillData : Record<string, string> = {};

		// Load data from sessionStorage if available
		this.filledWithSessionStorageData = false;
		if (window.sessionStorage) {
			for(const field in FIELDS) {
				if (sessionStorage.getItem(FIELDS[field].session)) {
					preFillData[field] = sessionStorage.getItem(FIELDS[field].session)!;
					this.filledWithSessionStorageData = true;
				}
			}

			(preFillData as any).nostreet = preFillData.hasOwnProperty('houseName');
			this.fire('fillFormWithStorageData', preFillData);
		}
	}

	private addParametersToLinks(update = false) {
		const $extensionInfoLink = this.$ctx.find('.extension-info a');
		const $overlay = this.$ctx.closest('.modOrderOverlay');
		const $orderLinks = $overlay.find('.modOrderOverlayOrderButtons a[href]');
		const $mobileLinks = $overlay.find('.orderoverlay-mobile-button-holder a:not([href^="tel:"])');
		const $links = $extensionInfoLink.add($orderLinks).add($mobileLinks);

		const mapping = {
			apartment: '',
			cancelUrl: document.location.href,
			city: this.response.address.city,
			floor: '',
			hdPossible: this.response.tvAvailability === 'available' ? 'true' : 'false',
			houseName: this.response.address.houseName,
			houseNumber: this.response.address.houseNumber,
			lang: this.language,
			locationId: this.response.address.locationId, // UniqueAddressID from GAIA
			returnUrl: document.location.href,
			scn : undefined as undefined | string,
			siebelProductId: '', // Will be set below
			socketNotInLivingArea: this.response.socketNotInLivingArea,
			street: this.response.address.street,
			technology: '', // Will be set below
			tvStreamsText: this.response.tvAvailabilityInfoText,
			uhdPossible: (this.response.tvAvailabilityStatus === 'uhd_box') ? 'true' : 'false',
			zip: this.response.address.zipCode4, // For M@P order form (with login)
			zipCode: this.response.address.zipCode4,
		};

		this.log('addParametersToLinks', $links, this.productIds, this.response);

		getProfile()
			.then(profile => {
				const user = profile.getUser();
				if(user) {
					// FIXME: Is this a good idea? We should not put PII in get params anyway…
					mapping.scn = user.getId();
				}
			})
			.catch();

		if (this.productIds.length > 0 && this.productIds[0].siebelId) {
			mapping.siebelProductId = this.productIds[0].siebelId;
		}

		// Set technology to one of these values <VDSL/BX/adsl/gv08>
		if (this.response.broadbandIsFiber) {
			mapping.technology = 'BX';
		} else if (this.response.technology === 'adsl') {
			mapping.technology = 'adsl';
		} else if (this.response.technology === 'vdsl') {
			mapping.technology = 'VDSL';
		} else {
			mapping.technology = 'gv08'; // Grundversorgung
		}

		if (this.doTracking) {
			$.each(SITECATALYST_BLOCKLISTED_URL_PARAMS, (_i, name) => {
				// tslint:disable-next-line: no-dynamic-delete
				delete mapping[name as keyof typeof mapping];
			});
		}

		// Rewrite links
		$links.each((_i, el) : void => {
			const $link = $(el);
			const href = $link.attr('href')!;
			const originalHref = update ? href : $link.data('original-href');
			const iso = $link.closest('div').hasClass('without-login');
			const isLego = /internet-/.test(href) && /tv-/.test(href) && /voip-/.test(href);

			if (isLego) {
				this.log('not rewriting link with hash parameter for Lego Configurator', href);

				return;
			}
			this.log('rewrite link', href, $link.attr('class'));
			// Save initial values
			$link.data('original-href', originalHref || href);
			// The no-login order form requires the parameter values to be encoded in ISO-8859-1
			const updatedHref = this.addParametersToUrl(originalHref, mapping, iso);
			if(updatedHref) {
				$link.attr('href', updatedHref);
			}
		});
	}

	/**
	 * Append parameters to URL which are not already present.
	 */
	private addParametersToUrl(url : string, mapping : Record<string, any>, iso = false) : string | undefined {
		this.log('addParametersToUrl', url, mapping, iso);
		if (!url) {
			return undefined;
		}
		// Handle registrationUrl (SAM) link: append parameters to the RURL parameter
		if (/[?#&]RURL=/.test(url)) {
			this.log('RURL handling', url);
			const rUrl = decodeURIComponent(url.match(/[?#&]RURL=([^&]*)/)![1]);
			const rUrlWithParams = this.addParametersToUrl(rUrl, mapping, iso)!;

			return SCS.Url.updateQueryStringParameter(url, 'RURL', encodeURIComponent(rUrlWithParams));
		}
		// Handle normal links (MAP)
		for (const key in mapping) {
			if (mapping.hasOwnProperty(key) && mapping[key] !== undefined && url.indexOf(key + '=') < 0) { // Do not overwrite an existing parameter
				this.log('addParameter', key, mapping[key]);
				url += /[?#]/.test(url) ? '&' : '?';
				if (iso) {
					// ISO-8859-1
					url += `${key}=${window.escape(mapping[key] || '')}`;
				} else {
					// UTF-8
					url += `${key}=${encodeURIComponent(mapping[key] || '')}`;
				}
			} else {
				this.log.debug('addParametersToUrl > SKIPPED parameter', key, mapping[key]);
			}
		}
		if (iso && /[?#]/.test(url)) {
			url += '&paramEncoding=ISO8859-1';
		}

		return url;
	}

	private adjustSubmitButtonText() {
		const $submitButton = this.$ctx.find('button[type=submit]');
		let productName = this.productTitlePlaceholder;

		if (this.productIds.length === 1) {
			// There could be two buttons in $submitButton, but the code works either way because all texts are the same.
			$submitButton.text($submitButton.data('product-check-text'));
			productName = this.productIds[0].name || this.productIds[0].siebelId;
		} else {
			$submitButton.text($submitButton.data('check-text'));
		}

		const $texts = this.$ctx.find('.instructions, .description');
		// Set every text individually because the texts might differ and we don’t want to set each one to the same value.
		$texts.each((_i, el) => {
			const $text = $(el);
			if (!$text.data('original-text')) {
				$text.data('original-text', $text.text());
			}
			$text.text($text.data('original-text').replace(/%product%/ig, productName));
		});
	}

	/**
	 * Tell the Order Overlay to reposition itself
	 */
	private centerOverlay() {
		this.fire('CenterOverlay');
	}


	// Module communication

	/**
	 * Sends updated mod.response JSON to listening modules,
	 * tracking scripts for Adserver (injected via Tealium Tag Manager),
	 * and Test&Target script (injected via mbox) for A/B-Testing.
	 */
	private sendDataUpdate(rawResponseData : ServiceAvailabilityResponse) {
		const event = 'ServiceAvailabilityDataUpdate';

		this.fire(event, this.response);
		this.$ctx.trigger(event, [this.response]); // Custom event for Test&Target and friends

		// Tracking with raw data
		const interaction = new SCS.portal.datalayer.Interaction()
			.withEvent('response', 'lineInfo', 'availabilitychecker', 'checker', 'lineInfo')
			.withRawData({
				response: rawResponseData,
			});
		this.log('sendDataUpdate > track', interaction);
		interaction.track();
	}

	/**
	 * Result or Form
	 */
	private sendStatusUpdate(state : 'form' | 'result') {
		this.fire('ServiceAvailabilityStatusUpdate', state);
	}

	/**
	 * allows product pages to define, what product(s) should be checked
	 */
	public onUpdateProductIds(data : {
		name : string;
		productIds : SiebelProduct[];
	}) {
		this.log('onUpdateProductIds', data);
		if (data && data.productIds && data.productIds.length > 0) {
			this.productIds = data.productIds;
		}
		this.adjustSubmitButtonText();
	}

	/**
	 * CheckProductIds (fired by Tc.Module.LegoOrderButton).
	 * Allows modules to fetch get service availability info for product ID(s).
	 * Note: The address info must already be saved in session storage.
	 * The result (mod.response object) is returned via success callback.
	 */
	public onCheckProductIds(data : {
		productIds : SiebelProduct[];
		success?(response : ServiceAvailabilityData) : void;
	}) {
		this.log('onCheckProductIds', data);
		if (data && data.productIds) {
			this.productIds = data.productIds;
		}
		this.readValuesSavedInSessionStorage();
		const jsonObject = this.prepareRequest();
		const success = data.success?.bind(data);

		callService(jsonObject, this.serviceUrl, response => {
			this.readServiceData(response); // Fills mod.response
			this.log('onCheckProductIds response', this.response);
			if (typeof success === 'function') {
				success(this.response);
			}
		});
	}

	/**
	 * allows product pages to define, what product should be checked
	 * works for one single product
	 */
	public onUpdateProductId(data : SiebelProduct) {
		this.log('onUpdateProductId', data);
		if (data && data.siebelId) {
			this.productIds = [data];
		}
		this.adjustSubmitButtonText();
		this.addParametersToLinks(true);
	}

	/**
	 * Order overlay opened in dialog => if fields are prefilled - submit
	 */
	public onDialogOpened() {
		if (!this.isInOverlay()) {
			return;
		}
		this.log('onDialogOpened');

		this.checkIsRunning = false;
		this.showForm();
		this.$contentCollapsable.addClass('hidden');
		this.hideAllResultElements();
		this.readValuesSavedInSessionStorage();

		const isValid = this.inputIsValid();

		if (isValid && this.filledWithSessionStorageData) {
			this.handleSubmit(
				{
					target: this.$ctx.find('.addressFields > form') as JQuery<HTMLFormElement>,
				},
			);
		}
	}

	// TODO: Seems unused, can it be removed?
	public onFormError(error? : string | null | Error) {
		if (error && typeof error === 'string') {
			this.$errordiv.removeClass('hidden');
			this.$errordiv.find('span').text(error);
		} else {
			this.$errordiv.addClass('hidden');
			this.$errordiv.find('span').text('');
		}
	}
}

export function callService(
	saObject : Request,
	serviceUrl : string,
	success : JQuery.Ajax.SuccessCallback<null>,
	customError : null | (() => void) = null,
) {
	const ajaxOptions : JQueryAjaxSettings = {};

	ajaxOptions.type = 'POST';
	ajaxOptions.data = JSON.stringify(saObject);
	ajaxOptions.contentType = 'application/json; charset=UTF-8';

	ajaxOptions.dataType = 'json';
	ajaxOptions.url = serviceUrl;

	let tries = 1;

	function error() {
		window.debug.log('callService > error', arguments, tries, customError);
		if (tries < 3) {
			tries += 1;
			setTimeout(() => {
				$.ajax(ajaxOptions);
			}, 1000);
		} else {
			if (customError) {
				customError();
			}
		}
	}

	ajaxOptions.success = success;
	ajaxOptions.error = error;
	$.ajax(ajaxOptions);
}

export function tvAvailability(response : null | string | ServiceAvailabilitySuccessResponse) : Availability | null {
	if (response === null) {
		return null;
	}
	if (typeof response === 'string') {
		return response as Availability;
	}
	const status = ((response.broadbandInfo || {}).tvAvailabilityInfo || {}).tvAvailabilityStatus || null;
	let result : Availability = 'not_available';
	if (status === 'sd_box' || status === 'web_only') {
		result = 'restricted';
	} else if (status === 'hd_box' || status === 'uhd_box') {
		result = 'available';
	}

	return result;
}

export function isValidResponse(response : ServiceAvailabilityData) {
	return response.addressValidation === 'valid' ||
		response.addressValidation === 'partiallyValid' ||
		response.addressValidation === 'adjusted';
}

function clearBadgeStatus($badgeContainer : JQuery, $badgeIcon : JQuery) {
	Object.keys(BADGES).forEach(key => {
		const badgeStatus = BADGES[key as keyof typeof BADGES];

		$badgeContainer.removeClass(badgeStatus.badgeClass);
		$badgeIcon.removeClass(badgeStatus.iconClass);
		$badgeIcon.addClass('hidden');
	});
}

/**
 * Sets a badge inside a service container (internet, tv, etc.)
 *
 * @param $serviceContainer - the service container, i.e. availability-internet
 * @param availability - (String) - the availability. Has to match any of the keys in the global badges variable ('check', 'alert')
 */
function showAvailabilityBadge(
	$serviceContainer : JQuery,
	availability : keyof typeof BADGES,
) {
	const $badgeContainer = $serviceContainer.find('.badge');
	const $badgeIcon = $badgeContainer.find('.badge__content .icon');
	const badgeStatus = BADGES[availability];

	clearBadgeStatus($badgeContainer, $badgeIcon);

	if(badgeStatus) {
		$badgeContainer.addClass(badgeStatus.badgeClass);
		$badgeIcon.addClass(badgeStatus.iconClass);
		$badgeIcon.removeClass('hidden');
	}
	// Trigger loaded event to reposition dialog
	$serviceContainer.trigger('changed.content');
}

function hasSessionStorageData() {
	if (!window.sessionStorage) {
		return false;
	}
	for (const field in FIELDS) {
		if (sessionStorage.getItem(FIELDS[field].session)) {
			return true;
		}
	}

	return false;
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/Tc.Module.ServiceAvailability.ts